/* eslint-disable sort-imports */
import Dialog from '@material-ui/core/Dialog'
import Typography from '@material-ui/core/Typography'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Button from '@material-ui/core/Button'
import CustomDialog from 'components/dialog'
import { terms } from 'common/terms'
import * as Const from 'common/constants'
import { useIntl } from 'react-intl'
import { useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { actionCreators } from 'actions'
import { AppDispatch } from 'store'

interface Props {
  open: boolean
  onClose: () => void
  finalFormat: string
  matchFormat: string
  mexicanoCourtSelection: string
  resultSorting: string
  teamFormat: string
  tournamentType: string
  newTournament: () => void
}

const SettingsInfoDialog = ({
  open,
  onClose,
  finalFormat,
  matchFormat,
  mexicanoCourtSelection,
  resultSorting,
  teamFormat,
  tournamentType,
  newTournament,
}: Props) => {
  const { formatMessage: fm } = useIntl()
  const [confirmationOpen, setConfirmationOpen] = useState(false)

  const mapTournamentType = (value: string) =>
    value === Const.AMERICANO ? fm(terms.americano) : fm(terms.mexicano)

  const mapTeamFormat = (value: string) =>
    value === Const.TeamFormat.Individual
      ? fm(terms.individually)
      : fm(terms.team)

  const mapMatchFormat = (value: string) => {
    const matchFormatMap: Record<string, string> = {
      [Const.FOR_11_POINTS]: fm(terms.play11Points),
      [Const.FOR_16_POINTS]: fm(terms.play16Points),
      [Const.FOR_21_POINTS]: fm(terms.play21Points),
      [Const.FOR_24_POINTS]: fm(terms.play24Points),
      [Const.FOR_32_POINTS]: fm(terms.play32Points),
      [Const.ANY_NBR_POINTS]: fm(terms.playPointsOrGames),
    }
    return matchFormatMap[value] || value
  }

  const mapFinalFormat = (value: string) => {
    const finalFormatMap: Record<string, string> = {
      [Const.ONE_AND_TWO_VS_THREE_AND_FOUR]: `1 & 2 ${fm(terms.versus)} 3 & 4`,
      [Const.ONE_AND_THREE_VS_TWO_AND_FOUR]: `1 & 3 ${fm(terms.versus)} 2 & 4`,
      [Const.ONE_AND_FOUR_VS_TWO_AND_THREE]: `1 & 4 ${fm(terms.versus)} 2 & 3`,
    }
    return finalFormatMap[value] || value
  }

  const mapResultSorting = (value: string) =>
    value === Const.POINTS_FIRST ? fm(terms.pointsFirst) : fm(terms.winsFirst)

  const mapCourtSelection = (value: string) =>
    value === Const.ASCENDING_COURT_ORDER
      ? fm(terms.ascendingCourtOrder)
      : fm(terms.random)

  const mapValueToLabel = (key: string, value: string): string => {
    const mappers: Record<string, (v: string) => string> = {
      tournamentType: mapTournamentType,
      teamFormat: mapTeamFormat,
      matchFormat: mapMatchFormat,
      finalFormat: mapFinalFormat,
      resultSorting: mapResultSorting,
      mexicanoCourtSelection: mapCourtSelection,
    }
    return mappers[key]?.(value) || value
  }

  const getSettings = () => {
    const settings = [
      {
        label: fm(terms.tournamentType),
        value: mapValueToLabel('tournamentType', tournamentType),
      },
      {
        label: fm(terms.teamFormat),
        value: mapValueToLabel('teamFormat', teamFormat),
      },
      {
        label: fm(terms.scoring),
        value: mapValueToLabel('matchFormat', matchFormat),
      },
      ...(teamFormat === Const.TeamFormat.Individual
        ? [
            {
              label: fm(terms.finalPairing),
              value: mapValueToLabel('finalFormat', finalFormat),
            },
          ]
        : []),
      {
        label: fm(terms.resultSorting),
        value: mapValueToLabel('resultSorting', resultSorting),
      },
      ...(tournamentType === Const.MEXICANO
        ? [
            {
              label: fm(terms.courtSelection),
              value: mapValueToLabel(
                'mexicanoCourtSelection',
                mexicanoCourtSelection
              ),
            },
          ]
        : []),
    ]
    return settings
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        disableTypography
        style={{ textAlign: 'center', paddingBottom: '8px' }}
      >
        <Typography variant="h3">{fm(terms.settingsInfo)}</Typography>
      </DialogTitle>
      <DialogContent
        style={{ padding: '20px', display: 'flex', justifyContent: 'center' }}
      >
        <table
          style={{
            borderCollapse: 'collapse',
            maxWidth: '400px',
            fontSize: '16px',
          }}
        >
          <tbody>
            {getSettings().map(({ label, value }, index) => (
              <tr key={index}>
                <td
                  style={{
                    padding: '8px 16px 8px 0',
                    fontWeight: 'bold',
                    verticalAlign: 'top',
                    width: '40%',
                  }}
                >
                  {label}:
                </td>
                <td
                  style={{
                    padding: '8px 0',
                    verticalAlign: 'top',
                    textAlign: 'right',
                  }}
                >
                  {value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'space-between' }}>
        <Button
          data-test-id="new-tournament"
          onClick={() => setConfirmationOpen(true)}
          color="secondary"
          variant="contained"
          style={{ fontSize: '14px' }}
        >
          {fm(terms.newTournament)}
        </Button>
        <Button onClick={onClose} color="primary" style={{ fontSize: '14px' }}>
          {fm(terms.ok)}
        </Button>
      </DialogActions>
      <CustomDialog
        confirmTestId="confirm-new-tournament"
        content={fm(terms.removeCurrentAndCreateNewTournament)}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={() => {
          newTournament()
          onClose()
        }}
        open={confirmationOpen}
      />
    </Dialog>
  )
}

const mapStateToProps = () => ({})

const mapDispatchToProps = (dispatch: AppDispatch) =>
  bindActionCreators(actionCreators, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(SettingsInfoDialog)
